import type { InkTrackState } from "@/components/visual/ink";
import { toDay } from "@/engine/date";
import type { ISODate, ProjectRecord } from "@/engine/types";
import { isRecordOverdue, recordPhase } from "@/store/record-ops";

/** 현황에 두는 이슈·리스크 추적. 열린 줄을 먼저 두고, 코멘트가 있는 해결은 마지막 한 줄이다. */
export interface StatusIssueTrack {
  id: string;
  title: string;
  opened: ISODate;
  state: InkTrackState;
  extent: number;
  comment?: string;
  attachments?: string[];
}

const OPEN_LIMIT = 4;

export function statusIssueTracks(records: ProjectRecord[], today: ISODate): StatusIssueTrack[] {
  const issues = records.filter((record) => record.kind === "issue" || record.kind === "risk");
  const open = issues
    .filter((record) => recordPhase(record.status) === "open")
    .sort((a, b) => {
      const late = Number(isRecordOverdue(b, today)) - Number(isRecordOverdue(a, today));
      if (late !== 0) return late;
      const high = Number(b.severity === "high") - Number(a.severity === "high");
      if (high !== 0) return high;
      return (a.due ?? "9999-12-31").localeCompare(b.due ?? "9999-12-31");
    })
    .slice(0, OPEN_LIMIT)
    .map((record) => {
      const late = isRecordOverdue(record, today);
      return {
        id: record.id,
        title: record.title || "제목 없음",
        opened: record.createdAt.slice(0, 10),
        state: late ? ("late" as const) : ("open" as const),
        extent: trackExtent(record, today, late),
        attachments: attachmentNames(record),
      };
    });

  const resolved = issues
    .filter((record) => recordPhase(record.status) === "closed")
    .map((record) => ({ record, comment: resolutionComment(record) }))
    .filter((item): item is { record: ProjectRecord; comment: string } => Boolean(item.comment))
    .sort((a, b) => (b.record.closedAt ?? b.record.updatedAt).localeCompare(a.record.closedAt ?? a.record.updatedAt))[0];

  if (!resolved) return open;
  return [
    ...open,
    {
      id: resolved.record.id,
      title: resolved.record.title || "제목 없음",
      opened: resolved.record.createdAt.slice(0, 10),
      state: "done",
      extent: 100,
      comment: resolved.comment,
      attachments: attachmentNames(resolved.record),
    },
  ];
}

function attachmentNames(record: ProjectRecord): string[] | undefined {
  const names = record.attachments.map((item) => item.title).filter(Boolean);
  return names.length > 0 ? names : undefined;
}

function resolutionComment(record: ProjectRecord): string | undefined {
  const resolution = record.sections.resolution?.trim();
  if (resolution) return resolution;
  const comment = [...record.comments].reverse().find((item) => !item.deleted && item.body.trim());
  return comment?.body.trim();
}

function trackExtent(record: ProjectRecord, today: ISODate, late: boolean): number {
  if (late) return 100;
  const start = record.createdAt.slice(0, 10);
  if (!record.due || record.due <= start) return 40;
  const span = toDay(record.due) - toDay(start);
  if (span <= 0) return 40;
  const gone = toDay(today) - toDay(start);
  return Math.max(8, Math.min(90, Math.round((gone / span) * 100)));
}
